import NoData from '@/components/common/NoData'
import ButtonField from '@/components/form-control/ButtonField'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CharacterPlusIcon,
} from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useSearchFlowSensor from '@/features/config/hooks/hydraulic/flowSensor/useSearchFlowSensor'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { Collapse, IconButton, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import FlowSensorTable from './FlowSensorTable'
import classes from './style.module.scss'

const FlowSensorView = (props: { flowRuleId: number }) => {
  const { t } = useTranslation()
  
  const { flowRuleId } = props
  const { interfaceId } = useAuthContext()
  const { data, refetch } = useSearchFlowSensor(Number(interfaceId))
  const [open, setOpen] = useState<boolean>(false)
  const { openSidebar, setSidebarView } = useConfigUI()
  const { openModal, setModalView } = useConfigUI()
  const { isViewOnly } = usePermission()

  const sensorList = useMemo(
    () => data?.list.filter((item) => item.flowRuleId === flowRuleId),
    [flowRuleId, data],
  )

  const handleAddSensor = () => {
    if (isViewOnly) return
    openSidebar()
    setSidebarView({ type: 'FLOW_SENSOR_VIEW', flowRuleId: flowRuleId })
  }

  const handleDeleteItem = (id: string, interfaceId: string) => {
    if (isViewOnly) return
    openModal()
    setModalView({
      type: 'DELETE_FLOW_SENSOR_VIEW',
      id: id,
      interfaceId: interfaceId,
      onSuccess: async () => {
        await refetch()
      },
    })
  }

  const handleEdit = (id: number) => {
    if (isViewOnly) return
    openSidebar()
    setSidebarView({
      type: 'FLOW_SENSOR_VIEW',
      id: id.toString(),
    })
  }

  const handleOpen = () => {
    setOpen(!open)
  }

  useEffect(() => {
    if (flowRuleId && sensorList) {
      setOpen(true)
    }
    //eslint-disable-next-line
  }, [sensorList])

  return (
    <Stack className={classes.sensorContainer}>
      <Stack className={classes.sensorHead}>
        <Stack className={classes.leftHeader}>
          <Typography className={classes.titleSensor}>{t('flowSensor')}</Typography>
          <IconButton
            aria-label="expand row"
            sx={{ padding: 0 }}
            onClick={handleOpen}
          >
            {open ? (
              <ArrowUpIcon sx={{ color: 'text.main' }} />
            ) : (
              <ArrowDownIcon sx={{ color: 'text.main' }} />
            )}
          </IconButton>
        </Stack>

        <Stack className={classes.rightHeader}>
          <ButtonField
            startIcon={<CharacterPlusIcon />}
            style={{
              height: '32px',
              minWidth: '135px',
              borderRadius: '5px',
            }}
            name= {t('commonActionsNewSensor')}
            disabled={isViewOnly}
            onClick={handleAddSensor}
          />
        </Stack>
      </Stack>
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        sx={{
          padding: '16px',
          border: '1px solid #D7D7D7',
          borderTop: 'unset',
        }}
      >
        {sensorList && sensorList?.length > 0 && (
          <FlowSensorTable
            rows={sensorList}
            onEditItem={handleEdit}
            onDeleteItem={handleDeleteItem}
          />
        )}

        {sensorList && sensorList.length === 0 && (
          <NoData text={ t('flowSensorDashboard.noFlowSensor') } height={'200px'} />
        )}
      </Collapse>
    </Stack>
  )
}

export default FlowSensorView
